import { formatDistanceToNowStrict } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import Label from 'src/components/Label';
import Iconify from 'src/components/Iconify';
import { cn } from 'src/@/lib/utils';
import { useTheme } from '@mui/material/styles';

const AVATAR_COLORS = [
  'bg-violet-600', 'bg-blue-600', 'bg-emerald-600', 'bg-amber-600',
  'bg-rose-600', 'bg-cyan-600', 'bg-indigo-600', 'bg-teal-600',
];

function getAvatarColor(name) {
  if (!name) return AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function getInitials(name) {
  if (!name) return '?';
  const names = name.split(' ');
  let initials = names[0].substring(0, 1).toUpperCase();
  if (names.length > 1) initials += names[names.length - 1].substring(0, 1).toUpperCase();
  return initials;
}

const STATUS_MAP = {
  'Atendendo': { color: 'bg-amber-500', label: 'Atendendo' },
  'Pendente': { color: 'bg-red-500', label: 'Em fila' },
  'Finalizado': { color: 'bg-green-500', label: 'Finalizado' },
  'Geral': { color: 'bg-blue-500', label: 'No bot' },
  'Avaliando': { color: 'bg-purple-500', label: 'Avaliando' },
};

const SENTIMENT_MAP = {
  1: { label: 'Raiva', icon: '😡' },
  2: { label: 'Frustração', icon: '😞' },
  3: { label: 'Neutro', icon: '😐' },
  4: { label: 'Satisfeito', icon: '😊' },
  5: { label: 'Muito Satisfeito', icon: '😄' },
};

export default function SupervisaoChatItem({ row, isSelected, onClick }) {
  const theme = useTheme();
  const {
    numeroCliente,
    nomeCliente,
    nome_cliente,
    dataCriacao,
    statusRoom,
    chatbot,
    lastUpdate,
    nome_setor,
    nome_usuario,
    sentiment,
  } = row;

  const displayName = nomeCliente || nome_cliente || numeroCliente;
  const status = STATUS_MAP[statusRoom] || { color: 'bg-gray-400', label: statusRoom };
  const sentimentInfo = SENTIMENT_MAP[sentiment];

  let timeAgo = '';
  try {
    if (lastUpdate) {
      timeAgo = formatDistanceToNowStrict(new Date(lastUpdate), { locale: ptBR, addSuffix: false });
    }
  } catch (e) {
    timeAgo = '';
  }

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-start gap-3 px-4 py-3 cursor-pointer transition-all border-l-[3px] border-b border-b-gray-100",
        isSelected
          ? "bg-violet-50 border-l-[#7C3AED]"
          : "bg-white border-l-transparent hover:bg-gray-50"
      )}
    >
      {/* Avatar */}
      <div className={cn(
        "w-9 h-9 min-w-[36px] rounded-full flex items-center justify-center text-white text-xs font-bold",
        getAvatarColor(displayName)
      )}>
        {getInitials(displayName)}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Row 1: Name + Time */}
        <div className="flex items-center justify-between gap-2">
          <span className="text-[13px] font-semibold text-gray-900 truncate">
            {displayName}
          </span>
          <span className="text-[10px] text-gray-400 whitespace-nowrap flex-shrink-0">
            {timeAgo}
          </span>
        </div>

        {/* Row 2: Numero + Status */}
        <div className="flex items-center gap-1.5 mt-0.5">
          <span className="text-[11px] text-gray-500 truncate">{numeroCliente}</span>
          <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", status.color)} />
          <span className="text-[10px] text-gray-500">{status.label}</span>
        </div>

        {/* Row 3: Atendente + Setor */}
        <div className="flex items-center gap-2 mt-1">
          {nome_usuario && (
            <div className="flex items-center gap-1 min-w-0">
              <Iconify icon="mdi:headset" width={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-[10px] text-gray-500 truncate">{nome_usuario}</span>
            </div>
          )}
          {nome_setor && (
            <div className="flex items-center gap-1 min-w-0">
              <Iconify icon="mdi:folder-outline" width={12} className="text-gray-400 flex-shrink-0" />
              <span className="text-[10px] text-gray-500 truncate">{nome_setor}</span>
            </div>
          )}
          {sentimentInfo && (
            <span className="text-[11px] flex-shrink-0" title={sentimentInfo.label}>
              {sentimentInfo.icon}
            </span>
          )}
          {chatbot && (
            <div className="flex items-center gap-0.5 flex-shrink-0">
              <Iconify icon="mdi:robot-outline" width={12} className="text-blue-400" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
